import moment from "moment";
import { WeekData } from "../types/rentalCarDatesRangeInput.types";

/**
 * Calculate the number of days for a week interval based on time comparison.
 * If dropoff time > pickup time: 6 days (e.g., pickup 9am, dropoff 5pm = same week, 6 days)
 * If dropoff time <= pickup time: 7 days (e.g., pickup 9pm, dropoff 12am = next day starts new week, 7 days)
 */
export const getWeekIntervalDays = (startTime?: string, endTime?: string): number => {
  // Default to 6 days if times are missing or invalid
  if (!startTime || !endTime || !startTime.includes(':') || !endTime.includes(':')) return 6;
  
  const startParts = startTime.split(':').map(Number);
  const endParts = endTime.split(':').map(Number);
  
  const startHour = startParts[0];
  const startMin = startParts[1] || 0;
  const endHour = endParts[0];
  const endMin = endParts[1] || 0;
  
  // Return default if any values are NaN
  if (isNaN(startHour) || isNaN(startMin) || isNaN(endHour) || isNaN(endMin)) return 6;
  
  const startTimeInMinutes = startHour * 60 + startMin;
  const endTimeInMinutes = endHour * 60 + endMin;
  
  // If dropoff time > pickup time: 6 days, else 7 days
  return endTimeInMinutes > startTimeInMinutes ? 6 : 7;
};

const KEY = "YYYY-MM-DD";

/** What a week is selected by: the day it starts, "YYYY-MM-DD". */
export const weekKey = (week: WeekData): string => moment(week.start).format(KEY);

/** The week that starts on `start` and runs `intervalDays`: the one shape every card, key and trip is built from. */
export const weekStartingOn = (
  start: moment.MomentInput,
  intervalDays: number,
  flags: { weekNum?: number; isPast?: boolean; isCurrentWeek?: boolean } = {},
): WeekData => {
  const from = moment(start).startOf("day");
  const to = from.clone().add(intervalDays, "days");
  return {
    weekNum: flags.weekNum ?? 0,
    start: from.toDate(),
    end: to.toDate(),
    isPast: !!flags.isPast,
    isCurrentWeek: !!flags.isCurrentWeek,
    effectiveStart: from.toDate(),
    effectiveEnd: to.toDate(),
  };
};

/**
 * The weeks of the month `date` falls in (1NOW-11212). ONE chain feeds every screen: the label on a card,
 * the key a click selects and the trip Save writes (getSelectedWeeksDateRange) are the same two dates.
 *
 * Weeks start 7 days apart from today (tomorrow when today is booked) and each runs `intervalDays`
 * (getWeekIntervalDays: 6 when the drop-off is later in the day than the pick-up, else 7). So weeks never
 * overlap and never skip a day, and nothing moves when a week is chosen or the times change: where a week
 * starts does not depend on either.
 */
export const getWeeksInMonth = (
  date: Date,
  unavailableDates: Date[] = [],
  intervalDays: number = 7,
): WeekData[] => {
  const startOfMonth = moment(date).startOf("month");
  const endOfMonth = moment(date).endOf("month").startOf("day");
  const today = moment().startOf("day");
  const todayIsUnavailable = unavailableDates.some((d) => moment(d).isSame(today, "day"));
  const anchor = todayIsUnavailable ? today.clone().add(1, "day") : today;

  // Week k starts k * 7 days from the anchor. Those that touch the month: it starts on or before the month's
  // last day and ends on or after its first.
  const firstWeek = Math.ceil((startOfMonth.diff(anchor, "days") - intervalDays) / 7);
  const lastWeek = Math.floor(endOfMonth.diff(anchor, "days") / 7);

  const weeks: WeekData[] = [];
  for (let k = firstWeek; k <= lastWeek; k++) {
    weeks.push(
      weekStartingOn(anchor.clone().add(7 * k, "days"), intervalDays, {
        weekNum: weeks.length + 1,
        isPast: k < 0,
        isCurrentWeek: k === 0,
      }),
    );
  }
  return weeks;
};

export function getAllWeeksForSelection(
  selectedWeeks: string[],
  unavailableDates: Date[] = [],
  intervalDays: number = 7,
): WeekData[] {
  if (selectedWeeks.length === 0) return [];

  const sorted = [...selectedWeeks].sort();
  const allWeeks: WeekData[] = [];
  const seenKeys = new Set<string>();

  const current = moment(sorted[0]).startOf("month");
  const last = moment(sorted[sorted.length - 1]).startOf("month");
  while (current.isSameOrBefore(last, "month")) {
    // The same week can appear in two months: keep it once.
    for (const week of getWeeksInMonth(current.toDate(), unavailableDates, intervalDays)) {
      const key = weekKey(week);
      if (!seenKeys.has(key)) {
        seenKeys.add(key);
        allWeeks.push(week);
      }
    }
    current.add(1, "month");
  }
  return allWeeks;
}

/** The week keys from `firstKey` to `lastKey`, 7 days apart: a selection with nothing left out. */
export const weekKeysBetween = (firstKey: string, lastKey: string): string[] => {
  const last = moment(lastKey, KEY);
  const keys: string[] = [];
  for (const day = moment(firstKey, KEY); day.isSameOrBefore(last, "day") && keys.length < 53; day.add(7, "days")) {
    keys.push(day.format(KEY));
  }
  return keys;
};

const clockOf = (time: string | undefined, fallbackHour: number, fallbackMin: number): [number, number] => {
  const [hour, min] = (time ?? "").split(":").map(Number);
  return time && time.includes(":") && !Number.isNaN(hour) && !Number.isNaN(min ?? 0) ? [hour, min || 0] : [fallbackHour, fallbackMin];
};

/**
 * The trip the selected weeks make. It starts on the first week's day at the pick-up time and runs one
 * interval (6 or 7 days, getWeekIntervalDays) for the first week and 7 for each after it, ending at the
 * drop-off time: the last selected card's label (getWeeksInMonth). `allWeeks` is not read any more: the keys
 * carry everything.
 */
export const getSelectedWeeksDateRange = (
  selectedWeeks: string[], // array of "YYYY-MM-DD"
  _allWeeks: WeekData[],
  startTime?: string, // "HH:mm" format
  endTime?: string, // "HH:mm" format
): { start: Date; end: Date } | null => {
  if (selectedWeeks.length === 0) return null;

  const [startHour, startMin] = clockOf(startTime, 9, 0);
  const [endHour, endMin] = clockOf(endTime, 17, 0);
  const days = 7 * (selectedWeeks.length - 1) + getWeekIntervalDays(startTime, endTime);

  const start = moment([...selectedWeeks].sort()[0], KEY).hour(startHour).minute(startMin).second(0).millisecond(0);
  const end = start.clone().add(days, "days").hour(endHour).minute(endMin).second(0).millisecond(0);
  return { start: start.toDate(), end: end.toDate() };
};

/**
 * Where a trip of `months` months from `start` ends (1NOW-11212): 30 days a month, and one day fewer when the
 * drop-off is later in the day than the pick-up. The backend counts a month this way (bookings/views.py,
 * handle_long_term_rental_payload) and prices it this way, so 1 month from Dec 1 at 3:00 / 3:00 ends on Dec 31.
 */
export const getMonthsEndDate = (start: Date, months: number, startTime?: string, endTime?: string): Date => {
  const dropOffIsLater = getWeekIntervalDays(startTime, endTime) === 6;
  return moment(start)
    .add(months * 30 - (dropOffIsLater ? 1 : 0), "days")
    .toDate();
};


export const weekContainsUnavailableDates = (
  week: WeekData,
  unavailableDates: Date[],
  partialUnavailability: { [key: string]: { from: string; to: string }[] } = {},
  allowPartialFirstWeek: boolean = false
): boolean => {
  const weekStart = moment(week.isCurrentWeek ? week.effectiveStart : week.start);
  const weekEnd = moment(week.isCurrentWeek ? week.effectiveEnd : week.end);
  if (allowPartialFirstWeek) {
    const totalDays = weekEnd.diff(weekStart, "days") + 1;
    let unavailableDaysCount = 0;
    let currentDay = weekStart.clone();
    while (currentDay.isSameOrBefore(weekEnd, "day")) {
      const dayStr = currentDay.format("YYYY-MM-DD");
      const isFullyUnavailable = unavailableDates.some(
        (unavailableDate) =>
          moment(unavailableDate).format("YYYY-MM-DD") === dayStr
      );
      const isPartiallyUnavailable = partialUnavailability[dayStr] !== undefined;
      
      if (isFullyUnavailable || isPartiallyUnavailable) {
        unavailableDaysCount++;
      }
      
      currentDay.add(1, "day");
    }
    return unavailableDaysCount === totalDays;
  }
  
  for (const unavailableDate of unavailableDates) {
    const unavailableMoment = moment(unavailableDate);
    if (
      unavailableMoment.isSameOrAfter(weekStart, "day") &&
      unavailableMoment.isSameOrBefore(weekEnd, "day")
    ) {
      return true;
    }
  }

  let currentDay = weekStart.clone();
  while (currentDay.isSameOrBefore(weekEnd, "day")) {
    const dayStr = currentDay.format("YYYY-MM-DD");
    // For the current week, allow partial unavailability on the FIRST day only
    // (user can still select the week, just restricted pickup times)
    const isFirstDayOfCurrentWeek = week.isCurrentWeek && currentDay.isSame(weekStart, "day");
    
    if (partialUnavailability[dayStr] !== undefined && !isFirstDayOfCurrentWeek) {
      return true;
    }
    currentDay.add(1, "day");
  }

  return false;
};
